/**
 * Utility functions for filter operations
 * Shared helper functions used across filter components
 */

/**
 * Filter items by search term across multiple keys
 * @param {Array} items - Items to filter
 * @param {string} searchTerm - Search term
 * @param {Array} keys - Keys to search in (e.g., ['name_ar', 'name_en'])
 * @returns {Array} - Filtered items
 */
export const filterBySearch = (items, searchTerm, keys = ['name_ar', 'name_en']) => {
  if (!searchTerm || !items || !Array.isArray(items)) {
    return items
  }

  const term = searchTerm.toLowerCase()
  
  return items.filter(item => {
    return keys.some(key => {
      const value = item[key]
      return value && value.toLowerCase().includes(term)
    })
  })
}

/**
 * Validate price range
 * @param {number|string} min - Minimum price
 * @param {number|string} max - Maximum price
 * @returns {Object} - Validation result {isValid, error}
 */
export const validatePriceRange = (min, max) => {
  const minVal = parseFloat(min)
  const maxVal = parseFloat(max)
  
  if (min && !max) {
    return {
      isValid: false,
      error: 'max_price_required'
    }
  }
  
  if (minVal >= maxVal && min !== "" && max) {
    return {
      isValid: false,
      error: 'min_must_be_less_than_max'
    }
  }
  
  return {
    isValid: true,
    error: null
  }
}

/**
 * Validate space range
 * @param {number|string} min - Minimum space
 * @param {number|string} max - Maximum space
 * @returns {Object} - Validation result {isValid, error}
 */
export const validateSpaceRange = (min, max) => {
  const minVal = parseFloat(min)
  const maxVal = parseFloat(max)
  
  if (min && !max) {
    return {
      isValid: false,
      error: 'max_space_required'
    }
  }
  
  if (minVal >= maxVal && min !== "" && max) {
    return {
      isValid: false,
      error: 'min_must_be_less_than_max'
    }
  }
  
  return {
    isValid: true,
    error: null
  }
}

/**
 * Find category by ID in nested structure
 * @param {Array} categories - Categories array
 * @param {number|string} id - Category ID to find
 * @returns {Object|null} - Found category or null
 */
export const findCategoryById = (categories, id) => {
  if (!categories || !Array.isArray(categories)) return null
  
  for (const category of categories) {
    if (category.id == id) {
      return category
    }
    
    if (category.children && Array.isArray(category.children)) {
      for (const child of category.children) {
        if (child.id == id) {
          return child
        }
      }
    }
  }
  
  return null
}

/**
 * Get category path (breadcrumb)
 * @param {Array} categories - Categories array
 * @param {number|string} id - Category ID
 * @returns {Array} - Path array [mainCategory, subCategory]
 */
export const getCategoryPath = (categories, id) => {
  if (!categories || !Array.isArray(categories)) return []
  
  for (const category of categories) {
    if (category.id == id) {
      return [category]
    }
    
    if (category.children && Array.isArray(category.children)) {
      for (const child of category.children) {
        if (child.id == id) {
          return [category, child]
        }
      }
    }
  }
  
  return []
}

/**
 * Toggle item in array
 * @param {Array} array - Target array
 * @param {*} item - Item to toggle
 * @returns {Array} - Updated array
 */
export const toggleItemInArray = (array, item) => {
  if (!Array.isArray(array)) return [item]
  
  const index = array.indexOf(item)
  if (index > -1) {
    return array.filter((_, i) => i !== index)
  } else {
    return [...array, item]
  }
}

/**
 * Update item state by ID
 * @param {Array} array - Array of items
 * @param {number|string} id - Item ID
 * @param {Object} state - State to update
 * @returns {Array} - Updated array
 */
export const updateItemState = (array, id, state) => {
  if (!Array.isArray(array)) return array
  
  return array.map(item => {
    if (item.id == id) {
      return { ...item, ...state }
    }
    return item
  })
}

/**
 * Check if attribute has nested attributes selected
 * @param {Object} child - Child attribute
 * @returns {boolean} - True if has selected nested attributes
 */
export const hasNestedAttributesSelected = (child) => {
  if (!child.attributes || !Array.isArray(child.attributes)) return false
  
  return child.attributes.some(nestedAttr => {
    if (!nestedAttr.children || !Array.isArray(nestedAttr.children)) return false
    return nestedAttr.children.some(nestedChild => nestedChild.isChecked)
  })
}

/**
 * Get filtered sub-categories based on search term
 * @param {Object} category - Category object
 * @param {string} searchTerm - Search term
 * @returns {Array} - Filtered subcategories
 */
export const getFilteredSubCategories = (category, searchTerm) => {
  if (!searchTerm || !category.children) {
    return category.children || []
  }
  
  const term = searchTerm.toLowerCase()
  return category.children.filter(sub => 
    sub.name_ar?.toLowerCase().includes(term) ||
    sub.name_en?.toLowerCase().includes(term)
  )
}

/**
 * Filter nested attributes based on search term
 * @param {Object} parent - Parent attribute
 * @param {string} searchTerm - Search term
 * @returns {Array} - Filtered children
 */
export const filterNestedAttributes = (parent, searchTerm) => {
  if (!searchTerm || !parent.children) {
    return parent.children || []
  }
  
  const term = searchTerm.toLowerCase()
  
  return parent.children.filter(child => {
    // Check if main child matches
    const mainChildMatches = child.name_ar?.toLowerCase().includes(term) ||
      child.name_en?.toLowerCase().includes(term)
    
    // Check if any nested attributes match
    let nestedMatches = false
    if (child.attributes && Array.isArray(child.attributes)) {
      nestedMatches = child.attributes.some(nestedAttr => {
        // Check nested attribute name
        const nestedAttrMatches = nestedAttr.name_ar?.toLowerCase().includes(term) ||
          nestedAttr.name_en?.toLowerCase().includes(term)
        
        // Check nested attribute children
        const nestedChildrenMatch = nestedAttr.children?.some(nestedChild =>
          nestedChild.name_ar?.toLowerCase().includes(term) ||
          nestedChild.name_en?.toLowerCase().includes(term)
        )
        
        return nestedAttrMatches || nestedChildrenMatch
      })
    }
    
    return mainChildMatches || nestedMatches
  })
}

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * Deep clone object
 * @param {*} obj - Object to clone
 * @returns {*} - Cloned object
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj
  
  try {
    return JSON.parse(JSON.stringify(obj))
  } catch (e) {
    console.error('Error cloning object:', e)
    return obj
  }
}

